import React, { useState } from "react";
import { connect } from "react-redux";
import ReactHtmlParser from "react-html-parser";
import shuffle from "lodash/shuffle";
import { confirmAlert } from "react-confirm-alert";
import AnswerType from "./answerType";
import { notifyError, notifySuccess } from "../../api";
import {
  flagQuestion,
  updateAnswer,
  updateAttempts,
  changeProgressData,
  toggleLoading,
} from "../../actions/quiz";
import { AppState } from "../../reducers";
import ErrorBoundary from "./errorBoundary";
import {
  checkButtonText,
  confirmBoxDesc,
  confirmBoxTitle,
  correctSoundUrl,
  finishButtonText,
  inCorrectSoundUrl,
  nextButtonText,
  userId,
} from "../../utils/constants";
import FlagButton from "../FlagButton";
import QuestionDetails from "./QuestionDetails";
import AnswerCheckButton from "../AnswerCheckButton";
import { allowSound } from "../../utils/functions";

const correctSound = new Audio(correctSoundUrl as string);
const inCorrectSound = new Audio(inCorrectSoundUrl as string);

export const Question = (props: any) => {
  const {
    question,
    dispatch,
    attempts,
    handleNext,
    answers,
    idx,
    goToNextUnsolved,
    count,
    handleFinish,
    finishQuizAnalyticData,
    flags,
    omt,
    omt_iframes,
    version,
    started,
    preview,
    id,
    loading,
    startQuizTime,
  } = props;
  const {
    _id,
    _question,
    _answerType,
    randomizer,
    string_compare = false,
    lesson_links,
    lesson_links_iframes,
    _answerData,
  } = question;

  const [answerValT, setAnswer] = useState(answers[_id]?.answerVal ?? "");
  const [startTime, setStartTime] = useState(new Date());
  const [sortAnswers, setSortAnswers] = React.useState(shuffle(_answerData));
  const [matrixSortAnswers, setMatrixSortAnswers] = React.useState(_answerData);
  const [randomMatrixAnswers, setRandomMatrixAnswers] =
    React.useState(_answerData);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const attemptsDis = attempts[_id] === 0 ? true : false;
  if (attemptsDis && flags.includes(_id) && !preview) {
    flagQuestion(_id);
  }

  const openConfirmBox = () => {
    confirmAlert({
      title: confirmBoxTitle || "",
      message: confirmBoxDesc || "",
      buttons: [
        {
          label: "Yes",
          onClick: () => handleFinish(true),
        },
        {
          label: "No",
          onClick: () => {
            handleFinish(false);
          },
        },
      ],
    });
  };

  const trimValue = (val: any) => {
    if (typeof val === "string") {
      return val.trim();
    } else if (Array.isArray(val)) {
      val = val.map((v) => v.trim());
      return val.filter((v: any) => {
        return v;
      });
    } else {
      return val;
    }
  };

  const getMatrixUserInput = (inputId) => {
    const item = matrixSortAnswers.find((ans) => ans._id === inputId);
    return item._sortString;
  };

  const getRandomMatrixUserInput = (inputId) => {
    const item = randomMatrixAnswers.find((ans) => ans._id === inputId);
    return item._id % 2 === 0 ? item._sortString : item._answer;
  };

  const playSound = (correctAnswer) => {
    if (correctAnswer === true) {
      if (allowSound()) {
        const playPromise = correctSound.play();
        if (playPromise !== undefined) {
          playPromise
            .then((_) => {
              notifySuccess("Correct Answer");
              setTimeout(() => {
                correctSound.pause();
                correctSound.currentTime = 0;
              }, 1500);
            })
            .catch((error) => {
              console.log("correctSound error>>>>>>>", error);
            });
        }
      } else {
        notifySuccess("Correct Answer");
      }
    } else if (correctAnswer === false) {
      if (allowSound()) {
        const playPromise = inCorrectSound.play();
        if (playPromise !== undefined) {
          playPromise
            .then((_) => {
              notifyError("Incorrect Answer");
              setTimeout(() => {
                inCorrectSound.pause();
                inCorrectSound.currentTime = 0;
              }, 1500);
            })
            .catch((error) => {
              console.log("inCorrectSound error>>>>>>>", error);
            });
        }
      } else {
        notifyError("Incorrect Answer");
      }
    }
  };

  const buttonText =
    attempts[_id] > 0 && !answers[_id]?.ans && !flags.includes(_id)
      ? checkButtonText
      : idx === count - 1
      ? finishButtonText
      : nextButtonText;

  const handleFinishPress = () => {
    if (flags.length > 0) {
      openConfirmBox();
    } else {
      finishQuizAnalyticData();
      handleFinish(false);
    }
  };

  const checkAnswer = (onEnterValue: any = "") => {
    setActiveQuestionId(_id);
    const start = startTime.getTime() / 1000;
    let oldDuration = 0;
    if (activeQuestionId !== _id) {
      oldDuration = answers[question._id]?.duration
        ? answers[question._id]?.duration
        : 0;
    }
    const currentTime = new Date().getTime() / 1000;
    const duration = Math.round(Math.abs(currentTime - start));
    let answerUpValue = onEnterValue
      ? trimValue(onEnterValue)
      : _answerType === "sort_answer"
      ? sortAnswers
      : _answerType === "matrix_sort_answer"
      ? randomizer
        ? randomMatrixAnswers
        : matrixSortAnswers
      : trimValue(answerValT);

    let ans = true;
    let correctCount = 0;
    let noAnswerInMatrix = true;
    let correctAnswer: any = "";
    switch (_answerType) {
      case "single":
        const correctAns = _answerData.find((ans: any) => ans._correct);
        ans = true; // if there's no correct option then default is true
        if (correctAns) {
          ans =
            String(correctAns._answer).toLowerCase().trim() ===
            String(answerUpValue).toLowerCase().trim()
              ? true
              : false;
          correctAnswer = ans;
        }
        break;
      case "cloze_answer":
        let qst = _answerData[0]?._answer;
        let matches = qst
          .match(/\{(.*?)\}/g)
          .map((val: any) => val.replace("{", "").replace("}", ""));
        let subMatches: any = [];
        let otherQVal: any = [];
        let otherMatches =
          matches && matches.length > 0
            ? matches.map((str: any) => {
                const strMatch = str.match(/\[(.*?)\]/g);
                if (!strMatch) {
                  subMatches.push({
                    type: "string",
                    val: String(ReactHtmlParser(str)[0]).toLowerCase().trim(),
                  });
                }
                if (strMatch && strMatch.length > 0) {
                  strMatch.map((val: any) =>
                    otherQVal.push(
                      String(
                        val.replace("[", "").replace("]", "")
                      ).toLowerCase()
                    )
                  );
                  subMatches.push({
                    type: "array",
                    val: otherQVal.map((val: any) =>
                      String(ReactHtmlParser(val)[0]).toLowerCase().trim()
                    ),
                  });
                  otherQVal = [];
                }
                return str;
              })
            : [];
        let qCheck = true;
        if (subMatches.length === answerUpValue.length && otherMatches) {
          subMatches.forEach((match: any, i: any) => {
            if (
              match.type === "string" &&
              String(answerUpValue[i]).toLowerCase().trim() === match.val &&
              qCheck
            ) {
              qCheck = true;
            } else if (
              match.type === "array" &&
              match.val.includes(
                String(answerUpValue[i]).toLowerCase().trim() || ""
              ) &&
              qCheck
            ) {
              qCheck = true;
            } else {
              qCheck = false;
            }
          });
        } else if (subMatches.length !== answerUpValue.length) {
          qCheck = false;
        }
        ans = qCheck;
        correctAnswer = ans;
        break;
      case "multiple":
        let checkMultiple = "";
        let correntAns = _answerData
          .filter((ans: any) => ans._correct)
          .map((obj: any) => obj._answer.trim());
        if (correntAns && correntAns.length === answerUpValue.length) {
          checkMultiple = correntAns.find(
            (ans: any) => !answerUpValue.includes(ans.trim())
          );
        }
        ans = checkMultiple === undefined ? true : false;
        correctAnswer = ans;
        break;
      case "sort_answer":
        ans = true;
        sortAnswers.forEach((answer, i) => {
          if (i !== answer._id) {
            ans = false;
          }
        });
        correctAnswer = ans;
        break;
      case "matrix_sort_answer":
        ans = false;
        if (randomizer) {
          if (string_compare) {
            randomMatrixAnswers.forEach((answer, i) => {
              if (answer._inputId !== null) {
                noAnswerInMatrix = false;
                const inputString = getRandomMatrixUserInput(answer._inputId);
                let compareToString =
                  answer._id % 2 === 0 ? answer._sortString : answer._answer;
                if (
                  inputString.trim() === compareToString.trim() ||
                  answer._inputId === answer._id
                ) {
                  correctCount = correctCount + 1;
                }
              }
            });
          } else {
            randomMatrixAnswers.forEach((answer, i) => {
              if (answer._inputId !== null) {
                noAnswerInMatrix = false;
                if (answer._inputId === answer._id) {
                  correctCount = correctCount + 1;
                }
              }
            });
          }
          if (correctCount === randomMatrixAnswers.length) {
            ans = true;
          }
        } else {
          if (string_compare) {
            matrixSortAnswers.forEach((answer, i) => {
              if (answer._inputId !== null) {
                noAnswerInMatrix = false;
                const inputString = getMatrixUserInput(answer._inputId);
                if (
                  inputString?.trim() === answer._sortString?.trim() ||
                  answer._inputId === answer._id
                ) {
                  correctCount = correctCount + 1;
                }
              }
            });
          } else {
            matrixSortAnswers.forEach((answer, i) => {
              if (answer._inputId !== null) {
                noAnswerInMatrix = false;
                if (answer._inputId === answer._id) {
                  correctCount = correctCount + 1;
                }
              }
            });
          }
          if (correctCount === matrixSortAnswers.length) {
            ans = true;
          }
        }
        correctAnswer = ans;
        break;
      default:
        let answer =
          _answerData[0] && _answerData[0]._answer
            ? _answerData[0]._answer
            : "";
        let answerValues = answer.trim().split("\n");
        ans = false;
        answerValues.forEach((ansVal: any) => {
          if (
            !ans &&
            String(ansVal).toLowerCase().trim() ===
              String(answerUpValue).toLowerCase().trim()
          ) {
            ans = true;
          }
        });
        correctAnswer = ans;
        break;
    }
    const updatedAnswers = { ...answers };
    updatedAnswers[question._id]["answerVal"] = answerUpValue;
    updatedAnswers[question._id]["ans"] = correctAnswer;
    updatedAnswers[question._id]["duration"] = duration + oldDuration;
    const updatedAttempts = { ...attempts };
    updatedAttempts[question._id] = updatedAttempts[question._id] - 1;
    if (
      !flags.includes(_id) &&
      (!answerUpValue ||
        (Array.isArray(answerUpValue) && answerUpValue.length === 0) ||
        (_answerType === "matrix_sort_answer" && noAnswerInMatrix))
    ) {
      notifyError("Please fill the answer");
      return;
    } else {
      dispatch(updateAttempts(_id));
      dispatch(
        updateAnswer(
          _id,
          ans,
          typeof answerUpValue === "number"
            ? String(answerUpValue)
            : answerUpValue,
          duration,
          _answerType
        )
      );
      if (
        updatedAttempts[_id] < 1 ||
        updatedAnswers[_id]?.ans ||
        flags.includes(_id)
      ) {
        setTimeout(() => {
          goToNextUnsolved();
        }, 1500);
      }
      let progressData = {
        userID: Number(userId),
        quizID: parseFloat(id),
        progress: {
          attempts: updatedAttempts,
          answers: updatedAnswers,
          flags,
          version: version,
          started: started,
          startQuizTime: startQuizTime || localStorage.getItem("QUIZ_STARTED"),
        },
        correctAnswer: correctAnswer,
      };
      dispatch(changeProgressData(progressData));
      playSound(correctAnswer);
    }
  };

  const saveProgress = () => {
    let newFlags = flags;
    let progressData = {};
    if (flags.includes(question._id)) {
      newFlags = newFlags.filter((f: string) => f !== question._id);
      notifySuccess("UnFlagged successfully");
    } else {
      newFlags = [...newFlags, question._id];
      notifySuccess("Flagged successfully");
    }
    const startTime = startQuizTime
      ? startQuizTime
      : localStorage.getItem("QUIZ_STARTED");

    progressData = {
      userID: Number(userId),
      quizID: parseFloat(id),
      progress: {
        attempts,
        answers: answers,
        flags: newFlags,
        version: version,
        started: started,
        startQuizTime: startTime,
      },
    };
    dispatch(toggleLoading(true));
    dispatch(changeProgressData(progressData));
  };

  return (
    <div id={"ref_" + _id}>
      <div className="question_head">
        {preview ? null : (
          <FlagButton
            isFlagged={flags.includes(_id)}
            disabled={loading || attempts[_id] < 1 || answers[_id]?.ans}
            onClick={(e) => {
              e.preventDefault();
              dispatch(flagQuestion(_id));
              saveProgress();
            }}
          />
        )}
      </div>
      <QuestionDetails
        question={_question}
        omt={omt}
        omt_iframes={omt_iframes}
        lessonLink={lesson_links}
        lesson_links_iframes={lesson_links_iframes}
        attempts={attempts[_id]}
      />
      <div className="answer_block">
        <ErrorBoundary>
          <AnswerType
            qId={_id}
            checkAnswer={checkAnswer}
            attemptsDis={attemptsDis || flags.includes(_id)}
            type={_answerType}
            isRandom={randomizer}
            answerData={_answerData}
            sortAnswers={sortAnswers}
            setSortAnswers={setSortAnswers}
            matrixSortAnswers={matrixSortAnswers}
            randomMatrixAnswers={randomMatrixAnswers}
            setRandomMatrixAnswers={setRandomMatrixAnswers}
            setMatrixSortAnswers={setMatrixSortAnswers}
            handleAnswerChange={setAnswer}
            answer={answerValT}
            preview={preview || answers[_id]?.ans}
          />
        </ErrorBoundary>
      </div>

      {!preview && _answerData ? (
        <AnswerCheckButton
          buttonText={buttonText}
          loading={loading}
          onClick={() => {
            if (buttonText === nextButtonText) {
              handleNext();
            } else if (buttonText === finishButtonText) {
              handleFinishPress();
            } else {
              checkAnswer();
            }
          }}
        />
      ) : null}
    </div>
  );
};
const mapStateToProps = (store: AppState) => {
  return {
    attempts: store.quiz.attempts,
    answers: store.quiz.answers,
    flags: store.quiz.flags,
    omt: store.quiz.data.omt,
    omt_iframes: store.quiz.data.omt_iframes,
    version: store.quiz.version,
    started: store.quiz.started,
    id: store.quiz.id,
    loading: store.quiz.loading,
    progressId: store.quiz.progressId,
    startQuizTime: store.quiz.startQuizTime,
  };
};
export default connect(mapStateToProps)(Question);
